import { Check, Cpu, Hand, X } from 'lucide-react';
import { useId, useState } from 'react';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn.ts';
import { formatInt } from '../lib/format.ts';
import { WORK_STATES, WORK_STATE_META, WORK_TONE_CLASS } from '../lib/workState.ts';
import type { WorkState } from '../lib/workState.ts';
import { GROUP_INDENT, GroupChevron } from './Group.tsx';
import { LiveNumber } from './live/LiveNumber.tsx';
import { Tooltip } from './Tooltip.tsx';

/**
 * The work-state primitives (status.md, "Work states"): the indicator, the label, the
 * grouped section header and the summary bar. Every page that lists work (sessions, runs,
 * landings, checkouts, missions) draws its states with these, so queued, running, needs you,
 * verifying, landed, failed and stale look the same everywhere.
 */

function Ring({ dotted, turning }: { dotted?: boolean; turning?: boolean }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" className="size-full" aria-hidden>
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeOpacity={dotted ? 0.55 : 0.28} strokeWidth="1.75" strokeDasharray={dotted ? '1.5 2.2' : undefined} strokeLinecap="round" />
      {turning && <path d="M8 2a6 6 0 0 1 6 6" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" className="origin-center animate-spin" />}
    </svg>
  );
}

function Dashed() {
  return (
    <svg viewBox="0 0 16 16" fill="none" className="size-full" aria-hidden>
      <circle cx="8" cy="8" r="5.5" stroke="currentColor" strokeWidth="1.5" strokeDasharray="2.6 2.2" />
    </svg>
  );
}

function Hollow() {
  return (
    <svg viewBox="0 0 16 16" fill="none" className="size-full opacity-70" aria-hidden>
      <circle cx="8" cy="8" r="4" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

/** The Land check: a ring and a check that strokes in once when `fresh`. */
function Landed({ fresh }: { fresh?: boolean }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" className="size-full" aria-hidden>
      <circle cx="8" cy="8" r="6.25" stroke="currentColor" strokeOpacity="0.35" strokeWidth="1.5" />
      <path d="M5.2 8.3l1.9 1.9 3.8-4.1" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" pathLength={1} className={cn(fresh && 'motion-land')} />
    </svg>
  );
}

export interface WorkStateIconProps {
  state: WorkState;
  /** 16 px in rows (default), 20 px in headers. */
  size?: 'sm' | 'md';
  /** The state was entered just now, live: Land strokes its check in, Fail fades its cross in. */
  fresh?: boolean;
  className?: string;
}

/** The state's icon in its colour. Needs you and failed sit on a soft disc: the two that ask for a person. */
export function WorkStateIcon({ state, size = 'sm', fresh, className }: WorkStateIconProps) {
  const meta = WORK_STATE_META[state];
  const tone = WORK_TONE_CLASS[meta.tone];
  const box = size === 'md' ? 'size-5' : 'size-4';
  if (state === 'needs-you' || state === 'failed') {
    const Icon = state === 'needs-you' ? Hand : X;
    return (
      <span data-work-state={state} className={cn('inline-flex shrink-0 items-center justify-center rounded-pill', box, tone.soft, tone.fg, fresh && state === 'failed' && 'motion-fail', className)} aria-hidden>
        <Icon className={size === 'md' ? 'size-3.5' : 'size-3'} strokeWidth={2.25} />
      </span>
    );
  }
  return (
    <span data-work-state={state} className={cn('inline-flex shrink-0', box, tone.fg, className)} aria-hidden>
      {state === 'running' ? <Ring turning /> : state === 'verifying' ? <Ring dotted turning /> : state === 'waiting-for-cpu' ? <Cpu className={size === 'md' ? 'size-5' : 'size-4'} /> : state === 'queued' ? <Dashed /> : state === 'landed' ? <Landed fresh={fresh} /> : <Hollow />}
    </span>
  );
}

export interface WorkStateLabelProps {
  state: WorkState;
  /** The word where the default does not fit: "Passed", "Done", "Idle". */
  label?: string;
  fresh?: boolean;
  /** Icon only: the label becomes the tooltip and the accessible name. */
  iconOnly?: boolean;
  className?: string;
}

/** A row's one status: the work-state icon plus its label, plain (never a filled pill in a row). */
export function WorkStateLabel({ state, label, fresh, iconOnly, className }: WorkStateLabelProps) {
  const meta = WORK_STATE_META[state];
  const text = label ?? meta.label;
  const loud = meta.tone === 'attention' || meta.tone === 'danger';
  const body = (
    <span
      data-status-pill=""
      data-variant="plain"
      data-tone={meta.tone}
      className={cn('inline-flex min-w-0 shrink-0 items-center gap-1.5 text-ui', className)}
      {...(iconOnly ? { role: 'img', 'aria-label': text, tabIndex: 0 } : {})}
    >
      <WorkStateIcon state={state} fresh={fresh} />
      {!iconOnly && <span className={cn('truncate', loud ? cn('font-medium', WORK_TONE_CLASS[meta.tone].fg) : meta.tone === 'neutral' ? 'text-fg-subtle' : WORK_TONE_CLASS[meta.tone].fg)}>{text}</span>}
    </span>
  );
  return iconOnly ? <Tooltip content={text}>{body}</Tooltip> : body;
}

export interface StateGroupProps {
  state: WorkState;
  count: number;
  /** One meta fact for the header: "oldest 14m", "last landed 2h ago". */
  meta?: ReactNode;
  /** Override the state's word: "Running now", "In the landing queue". */
  label?: string;
  /** Start collapsed; default from the state (landed and stale collapse). */
  defaultCollapsed?: boolean;
  /** Trailing header controls. */
  actions?: ReactNode;
  /** A stable id for the header, when two groups on a page share a state (Idle and Ended are both stale). */
  groupId?: string;
  children: ReactNode;
  className?: string;
}

/**
 * A group of work in one state: a header row (icon, label, live count, one meta fact) and the
 * items under it. Finished and quiet groups start collapsed; the header toggles them. The items
 * sit one step in (GROUP_INDENT), so a row's icon lines up under the header's icon; give rows
 * the header's left padding (`px-row-x`).
 */
export function StateGroup({ state, count, meta, label, defaultCollapsed, actions, groupId, children, className }: StateGroupProps) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed ?? WORK_STATE_META[state].collapsed);
  const text = label ?? WORK_STATE_META[state].label;
  // Unique per group: a page can hold the same state in many sections (one per project).
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const id = `state-group-${groupId ?? `${state}-${uid}`}`;
  return (
    <section aria-labelledby={id} data-state-group={groupId ?? state} className={cn('flex min-w-0 flex-col', className)}>
      <div className="flex h-(--group-header-height) min-w-0 items-center gap-2 pr-2">
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={!collapsed}
          className="group/sg flex h-full min-w-0 flex-1 items-center gap-2 rounded-control pl-row-x text-left -outline-offset-2 hover:bg-hover"
        >
          <GroupChevron open={!collapsed} />
          <WorkStateIcon state={state} />
          <span id={id} className="truncate text-ui font-medium text-fg">
            {text}
          </span>
          <LiveNumber value={count} className="text-ui text-fg-subtle" />
          {meta != null && <span className="hidden min-w-0 truncate text-caption text-fg-subtle sm:inline">{meta}</span>}
        </button>
        {actions != null && <span className="flex shrink-0 items-center gap-1">{actions}</span>}
      </div>
      {!collapsed && (
        // On a phone the rows take the full width, as in the Inbox and Sessions: the step costs a title too much.
        <div data-group-body="" className={cn('flex min-w-0 flex-col', GROUP_INDENT, 'max-sm:pl-0')}>
          {children}
        </div>
      )}
    </section>
  );
}

export interface StateBarProps {
  counts: Partial<Record<WorkState, number>>;
  /** Accessible summary when the bar stands alone: "6 running, 2 need you, 4 idle". Omit inside a labelled group. */
  label?: string;
  className?: string;
}

/**
 * The bar alone: the share of work in each state as one segmented track, segments in the order
 * of attention. Segments move by transform as counts change (never width).
 */
export function StateBar({ counts, label, className }: StateBarProps) {
  const total = WORK_STATES.reduce((n, s) => n + (counts[s] ?? 0), 0);
  let offset = 0;
  return (
    <div className={cn('relative h-1.5 overflow-hidden rounded-pill bg-active', className)} role={label ? 'img' : undefined} aria-label={label} aria-hidden={label ? undefined : true}>
      {WORK_STATES.map((s) => {
        const share = total ? (counts[s] ?? 0) / total : 0;
        const x = offset;
        offset += share;
        return (
          <span
            key={s}
            className={cn('motion-segment absolute inset-y-0 left-0 w-full', WORK_TONE_CLASS[WORK_STATE_META[s].tone].bar, s === 'verifying' && 'opacity-60', s === 'stale' && 'opacity-50')}
            style={{ translate: `${x * 100}% 0`, scale: `${share} 1` }}
          />
        );
      })}
    </div>
  );
}

export interface StateSummaryBarProps {
  counts: Partial<Record<WorkState, number>>;
  /** Override labels per state ("Passed" for landed on a test board). */
  labels?: Partial<Record<WorkState, string>>;
  /** Makes each legend entry a filter. */
  onSelect?: (state: WorkState) => void;
  /** The selected filter, if any. */
  selected?: WorkState | null;
  /** Accessible name: "Checkouts by state". */
  label: string;
  className?: string;
}

/**
 * The share of work in each state as one segmented bar, and a legend of live counts under it
 * ("2 Needs you, 16 Running, 9 Queued"). Segments move by transform as counts change (never
 * width), and a state with no work drops out of the legend.
 */
export function StateSummaryBar({ counts, labels, onSelect, selected, label, className }: StateSummaryBarProps) {
  const present = WORK_STATES.filter((s) => (counts[s] ?? 0) > 0);
  return (
    <div className={cn('flex min-w-0 flex-col gap-3', className)} role="group" aria-label={label}>
      <StateBar counts={counts} />
      <ul className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1">
        {present.length === 0 && <li className="text-caption text-fg-subtle">Nothing yet</li>}
        {present.map((s) => {
          const body = (
            <>
              <WorkStateIcon state={s} />
              <LiveNumber value={counts[s] ?? 0} className="text-ui font-medium text-fg" />
              <span className="text-caption text-fg-muted">{labels?.[s] ?? WORK_STATE_META[s].label}</span>
            </>
          );
          return (
            <li key={s} className="motion-arrive">
              {onSelect ? (
                <button
                  type="button"
                  onClick={() => onSelect(s)}
                  aria-pressed={selected === s}
                  aria-label={`${formatInt(counts[s] ?? 0)} ${labels?.[s] ?? WORK_STATE_META[s].label}`}
                  className={cn('flex h-control-sm items-center gap-1.5 rounded-control px-1.5 -mx-1.5 hover:bg-hover', selected === s && 'bg-active')}
                >
                  {body}
                </button>
              ) : (
                <span className="flex items-center gap-1.5">{body}</span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
